import React, { useState, useRef } from "react";
import { brand, services, team, faqs, contactConfig } from "../mock";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Shield, Server, Network, KeyRound, Lock, Mail, Check, ArrowRight, ArrowUpRight, User, Building2, Tag, MessageSquare, Send, ShieldCheck, MapPin, Clock } from "lucide-react";
import ServiceDialog from "./ServiceDialog";
import PackagesSection from "./PackagesSection";
import ExitIntentPopup from "./ExitIntentPopup";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import privacyContent from "../privacy";
import { AnimateOnScroll } from "../hooks/useScrollAnimation";
import { SectionHeading } from "./SectionHeading";

const iconMap = { Shield, Server, Network, KeyRound, Lock };

/* ---------------------------------- HERO ---------------------------------- */

function StatusPanel({ accent }) {
  const rows = [
    { label: "Active Directory", status: "gehärtet", pct: 92, delay: 0.2 },
    { label: "Microsoft 365", status: "abgesichert", pct: 88, delay: 0.35 },
    { label: "Berechtigungen", status: "minimiert", pct: 95, delay: 0.5 },
    { label: "Schwachstellen", status: "geschlossen", pct: 90, delay: 0.65 },
  ];
  return (
    <div className="relative">
      <div
        className="pointer-events-none absolute -inset-8 rounded-[3rem]"
        style={{ background: "radial-gradient(60% 50% at 70% 20%, rgba(40,216,255,0.16), rgba(2,6,23,0) 70%)" }}
        aria-hidden
      />
      <div data-testid="hero-status-panel" className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#131C2D]/85 shadow-[0_30px_80px_rgba(0,0,0,0.5)] backdrop-blur-xl">
        {/* Terminal header */}
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-3.5">
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]/80" />
          </div>
          <span className="font-mono text-[11px] tracking-wide text-slate-400">lazote://security-status</span>
          <span className="inline-flex items-center gap-1.5 font-mono text-[10px] font-medium uppercase tracking-wider text-slate-300">
            <span className="pulse-dot inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: accent }} />
            Live
          </span>
        </div>

        {/* Rows */}
        <div className="space-y-5 p-5 md:p-6">
          {rows.map((r) => (
            <div key={r.label}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-100">{r.label}</span>
                <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider" style={{ borderColor: "rgba(40,216,255,0.30)", color: accent }}>
                  <Check size={11} strokeWidth={3} />
                  {r.status}
                </span>
              </div>
              <div className="mt-2.5 h-1 overflow-hidden rounded-full bg-white/10">
                <div
                  className="status-fill h-full rounded-full"
                  style={{ width: `${r.pct}%`, background: "linear-gradient(90deg, #002D72, #28D8FF)", animationDelay: `${r.delay}s` }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-white/10 px-5 py-3.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">Baseline: CIS / NIST</span>
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: accent }}>Status: gehärtet</span>
        </div>
      </div>
    </div>
  );
}

const tickerItems = ["CIS Benchmarks", "NIST Framework", "Active Directory", "Microsoft 365", "Zero Trust", "Least Privilege", "Hardening Sprints", "Schweizer Qualität"];

function HeroTicker() {
  const items = [...tickerItems, ...tickerItems];
  return (
    <div className="relative border-t border-white/10">
      <div className="overflow-hidden py-4 [mask-image:linear-gradient(90deg,transparent,#000_12%,#000_88%,transparent)]">
        <div className="marquee-track gap-10">
          {items.map((t, i) => (
            <span key={i} className="flex items-center gap-10 whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
              {t}
              <span className="h-1 w-1 rounded-full bg-[#28D8FF]/60" />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function HeroSection({ accent, onScrollTo }) {
  return (
    <section className="relative overflow-hidden bg-[#0A111F] text-white">
      <div className="pointer-events-none absolute inset-0 hero-grid" aria-hidden />
      <div className="pointer-events-none absolute -top-32 right-0 h-[520px] w-[520px] rounded-full" style={{ background: "radial-gradient(circle, rgba(40,216,255,0.10), rgba(40,216,255,0) 70%)" }} aria-hidden />
      <div className="pointer-events-none absolute bottom-0 left-1/4 h-[320px] w-[640px] rounded-full" style={{ background: "radial-gradient(circle, rgba(0,45,114,0.45), rgba(2,6,23,0) 70%)" }} aria-hidden />
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="hero-scan absolute inset-x-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(40,216,255,0.22), transparent)" }} />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 pt-36 md:px-12 md:pt-48 pb-20 md:pb-24">
        <div className="grid grid-cols-1 items-center gap-14 md:grid-cols-2">
          <AnimateOnScroll animation="fade-up" duration={800}>
            <div className="inline-flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-[#28D8FF]">
              <span className="h-px w-8 bg-[#28D8FF]/60" />
              Microsoft Security Hardening
            </div>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Härte deine Umgebung,{" "}
              <span style={{ color: accent }}>bevor es ein Angreifer tut.</span>
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-slate-300 md:max-w-xl">
              Wir härten Windows, Active Directory und Microsoft 365 – pragmatisch, normenkonform und messbar. Damit Angriffe ins Leere laufen.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button
                data-testid="hero-primary-cta"
                onClick={() => onScrollTo("kontakt")}
                className="group rounded-full px-6 py-6 text-base font-semibold shadow-[0_0_15px_rgba(40,216,255,0.2)] transition-shadow hover:shadow-[0_0_28px_rgba(40,216,255,0.55)]"
                style={{ backgroundColor: accent, color: "#063247" }}
              >
                Kostenloses Erstgespräch
                <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-0.5" />
              </Button>
              <Button
                data-testid="hero-secondary-cta"
                variant="outline"
                onClick={() => onScrollTo("services")}
                className="rounded-full border-white/25 bg-white/5 px-6 py-6 text-base text-white transition-colors hover:border-[#28D8FF] hover:bg-white/10 hover:text-white"
              >
                Leistungen ansehen
              </Button>
            </div>
            <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-400">
              {["CIS/NIST-orientiert", "Schweiz", "Hands-on & pragmatisch"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <span className="grid h-4 w-4 place-items-center rounded-full" style={{ backgroundColor: "rgba(40,216,255,0.18)" }}>
                    <Check size={11} strokeWidth={3} style={{ color: accent }} />
                  </span>
                  {t}
                </span>
              ))}
            </div>
          </AnimateOnScroll>
          <AnimateOnScroll animation="fade-left" delay={200} duration={800} className="relative">
            <StatusPanel accent={accent} />
          </AnimateOnScroll>
        </div>
      </div>

      <HeroTicker />
    </section>
  );
}

/* -------------------------------- SERVICES -------------------------------- */

function ServiceCard({ s, onMore, index = 0 }) {
  const Icon = iconMap[s.icon] || Shield;
  return (
    <div
      data-testid={`service-card-${s.id}`}
      onClick={() => onMore(s)}
      className="group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border border-transparent bg-[#F4F6F8] p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[#28D8FF]/50 hover:bg-white hover:shadow-[0_24px_60px_rgba(2,6,23,0.10)] md:p-10"
    >
      <span className="pointer-events-none absolute -right-2 -top-5 select-none font-mono text-[5.5rem] font-semibold leading-none text-[#0A111F]/[0.045] transition-colors duration-300 group-hover:text-[#28D8FF]/15" aria-hidden>
        0{index + 1}
      </span>

      <div className="relative flex items-start justify-between">
        <div className="grid h-14 w-14 place-items-center rounded-2xl text-white shadow-sm transition-transform duration-300 group-hover:scale-105" style={{ background: "linear-gradient(135deg, #002D72, #28D8FF)" }}>
          <Icon size={24} />
        </div>
        <span className="grid h-9 w-9 place-items-center rounded-full border border-slate-300 text-slate-400 transition-all duration-300 group-hover:border-[#002D72] group-hover:bg-[#002D72] group-hover:text-white">
          <ArrowUpRight size={16} />
        </span>
      </div>

      <h3 className="relative mt-6 font-display text-xl font-bold text-[#0A111F] md:text-2xl">{s.title}</h3>
      <p className="relative mt-1.5 text-sm text-slate-600">{s.punchline}</p>

      <ul className="relative mt-6 space-y-2.5 text-sm text-slate-700">
        {s.bullets.map((b, idx) => (
          <li key={idx} className="flex items-start gap-2.5">
            <span className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full" style={{ backgroundColor: "rgba(40,216,255,0.18)" }}>
              <Check size={11} strokeWidth={3} className="text-[#002D72]" />
            </span>
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <div className="relative mt-auto flex items-center justify-between gap-3 pt-8">
        <button
          data-testid={`service-more-${s.id}`}
          onClick={(e) => { e.stopPropagation(); onMore(s); }}
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#002D72]"
        >
          Mehr erfahren
          <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
        </button>
        <button
          data-testid={`service-angebot-${s.id}`}
          onClick={(e) => { e.stopPropagation(); document.getElementById("angebote")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
          className="font-mono text-[11px] font-medium uppercase tracking-wider text-slate-500 transition-colors hover:text-[#002D72]"
        >
          Passendes Angebot →
        </button>
      </div>
    </div>
  );
}

function ServicesSection({ onMore }) {
  return (
    <section id="services" className="scroll-mt-24 bg-white">
      <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
        <AnimateOnScroll animation="fade-up">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <SectionHeading align="left" overline="Unsere Dienstleistungen" title="Was wir für dich härten" />
            <p className="max-w-sm text-slate-600 lg:pb-1.5 lg:text-right">Pragmatisch umgesetzt. Standardkonform dokumentiert.</p>
          </div>
        </AnimateOnScroll>

        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8">
          {services.map((s, index) => (
            <AnimateOnScroll key={s.id} animation="fade-up" delay={index * 80}>
              <ServiceCard s={s} onMore={onMore} index={index} />
            </AnimateOnScroll>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------- TEAM ---------------------------------- */

function TeamSection() {
  return (
    <section id="team" className="scroll-mt-24 bg-white">
      <div className="mx-auto max-w-7xl px-6 pt-20 pb-24 md:px-12 md:pt-32 md:pb-32">
        <AnimateOnScroll animation="fade-up">
          <SectionHeading overline="Team" title="Menschen hinter l'azote" kicker="Hands-on, zuverlässig, schweizerisch." />
        </AnimateOnScroll>
        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {team.map((m, index) => (
            <AnimateOnScroll key={m.name} animation="fade-up" delay={index * 100}>
              <div data-testid={`team-card-${index + 1}`} className="group h-full">
                <div className="relative overflow-hidden rounded-2xl bg-[#F4F6F8]">
                  <div className="aspect-[3/4] w-full overflow-hidden">
                    <img
                      src={m.image}
                      alt={m.name}
                      className="h-full w-full object-cover object-center grayscale transition-all duration-500 group-hover:scale-[1.03] group-hover:grayscale-0"
                    />
                  </div>
                  <span className="absolute left-4 top-4 rounded-full bg-[#0A111F]/70 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-[#28D8FF] backdrop-blur">
                    0{index + 1}
                  </span>
                </div>
                <div className="px-1 pt-4">
                  <div className="font-display font-semibold text-[#0A111F]">{m.name}</div>
                  <div className="mt-0.5 text-sm text-slate-600">{m.role}</div>
                </div>
              </div>
            </AnimateOnScroll>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------- QUOTE --------------------------------- */

function QuoteSection({ accent }) {
  const [logoOk, setLogoOk] = useState(true);
  const src = brand.smallLogoUrl || brand.logoUrl;
  return (
    <section id="quote" className="relative overflow-hidden bg-[#002D72]">
      <span className="pointer-events-none absolute -top-8 left-4 select-none font-serif text-[14rem] leading-none text-[#28D8FF]/10 md:left-12 md:text-[18rem]" aria-hidden>
        “
      </span>
      <div className="pointer-events-none absolute -bottom-24 -right-16 h-72 w-72 rounded-full" style={{ background: "radial-gradient(circle, rgba(40,216,255,0.25), rgba(40,216,255,0) 70%)" }} aria-hidden />
      <div className="relative mx-auto max-w-4xl px-6 py-24 text-center md:px-12 md:py-32">
        <AnimateOnScroll animation="scale" duration={700}>
          {src && logoOk ? (
            <img
              src={src}
              alt="l'azote"
              className="mx-auto mb-8 h-16 w-auto brightness-0 invert opacity-95 md:h-20"
              onError={() => setLogoOk(false)}
            />
          ) : null}
          <p className="font-display text-2xl font-semibold leading-snug text-white sm:text-3xl md:text-4xl">
            Wie man in der Schweiz sagt „<span style={{ color: accent }}>die Schwache nimmts</span>“. Also sind sie nicht eines dieser Unternehmen. Wenden Sie sich an uns.
          </p>
        </AnimateOnScroll>
      </div>
    </section>
  );
}

/* ---------------------------------- NEWS ---------------------------------- */

const newsItems = [
  {
    testid: "news-card-1",
    tag: "News",
    date: "Februar 2026",
    title: "Neue Website jetzt live",
    text: "Unsere komplett überarbeitete Website ist online – klarer Fokus auf Microsoft Security Hardening, modernes Design, schnellere Ladezeiten und ein direkter Draht zu unserem Team.",
    image: "/news-website.jpg",
    alt: "Screenshot der neuen l'azote Website",
    imagePos: "object-[50%_18%]",
  },
  {
    testid: "news-card-2",
    tag: "Referenz",
    date: "Kürzlich",
    title: "Portanova Schule Zürich",
    text: "IT‑Infrastrukturprojekt erfolgreich abgeschlossen: Planung, Härtung und Umsetzung einer stabilen, sicheren Umgebung für den Schulbetrieb.",
    image: "https://www.privatschule.ch/temp/compressed_resize_1920x1280_DSC_9328.webp",
    alt: "Portanova Schule Zürich",
    imagePos: "object-[50%_55%]",
  },
];

function NewsCard({ item, flipped }) {
  return (
    <article
      data-testid={item.testid}
      className={`group flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(2,6,23,0.12)] md:flex-row ${flipped ? "md:flex-row-reverse" : ""}`}
    >
      <div className="relative h-56 w-full overflow-hidden md:h-auto md:w-2/5 md:min-h-[280px]">
        <img
          src={item.image}
          alt={item.alt}
          className={`absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 ${item.imagePos || ""}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A111F]/30 to-transparent md:bg-gradient-to-r" />
      </div>
      <div className="flex flex-1 flex-col justify-center p-7 md:p-10">
        <div className="flex items-center gap-3">
          <span className="rounded-full px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-[#063247]" style={{ backgroundColor: "#28D8FF" }}>
            {item.tag}
          </span>
          <span className="font-mono text-[11px] uppercase tracking-wider text-slate-500">{item.date}</span>
        </div>
        <h3 className="mt-4 font-display text-2xl font-bold text-[#0A111F]">{item.title}</h3>
        <p className="mt-2.5 leading-relaxed text-slate-600">{item.text}</p>
      </div>
    </article>
  );
}

function NewsSection() {
  return (
    <section id="news" className="border-y border-slate-200/70 bg-[#F4F6F8]">
      <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
        <AnimateOnScroll animation="fade-up">
          <SectionHeading overline="News" title="Aktuelles aus dem Team" kicker="Einblicke in Projekte und Neuigkeiten von l'azote." />
        </AnimateOnScroll>
        <div className="mt-14 space-y-8">
          {newsItems.map((item, i) => (
            <AnimateOnScroll key={item.testid} animation="fade-up" delay={i * 120}>
              <NewsCard item={item} flipped={i % 2 === 1} />
            </AnimateOnScroll>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- CONTACT -------------------------------- */

function ContactSection({ accent }) {
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [company, setCompany] = React.useState("");
  const [subject, setSubject] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    try {
      window.__setContactSubjectPreset = (val) => setSubject(val || "");
    } catch (_) {}
    return () => {
      try { delete window.__setContactSubjectPreset; } catch (_) {}
    };
  }, []);

  const openMail = () => {
    if (!email.trim() || !message.trim()) {
      setError("Bitte gib mindestens deine E‑Mail und eine kurze Nachricht an.");
      return;
    }
    setError("");
    const to = contactConfig.emailFallback || "info@lazote.ch";
    const sub = encodeURIComponent(subject || "Kontaktanfrage");
    const lines = [
      `Name: ${name || "—"}`,
      `Firma: ${company || "—"}`,
      `E-Mail: ${email || "—"}`,
      "",
      message || "Hallo l'azote, bitte um Rückmeldung.",
      "",
      "Freundliche Grüsse",
      name || "",
    ];
    const body = encodeURIComponent(lines.join("\n"));
    window.location.href = `mailto:${to}?subject=${sub}&body=${body}`;
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-slate-900 placeholder:text-slate-400 transition-all duration-200 hover:border-slate-300 focus:border-[#28D8FF] focus:outline-none focus:ring-2 focus:ring-[#28D8FF]/30";
  const labelCls = "mb-1.5 flex items-center gap-1.5 text-sm font-medium text-slate-700";
  const navy = brand.colors.navy;

  return (
    <section id="kontakt" className="scroll-mt-24 bg-white">
      <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
        <AnimateOnScroll animation="fade-up">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <SectionHeading align="left" overline="Kontakt" title="Lass uns über deine Sicherheit sprechen" />
            <p className="max-w-sm text-slate-600 lg:pb-1.5 lg:text-right">Schick uns ein paar Stichworte – wir kommen proaktiv mit Vorschlägen auf dich zu.</p>
          </div>
        </AnimateOnScroll>

        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* Left rail: direct contact + FAQ */}
          <aside className="order-2 flex flex-col gap-8 lg:order-1 lg:col-span-5">
            <AnimateOnScroll animation="fade-right" delay={100}>
              <div data-testid="contact-info-card" className="relative overflow-hidden rounded-2xl p-8 text-white shadow-[0_24px_60px_rgba(0,45,114,0.25)]" style={{ background: "linear-gradient(135deg, #0A111F 0%, #002D72 140%)" }}>
                <div className="pointer-events-none absolute -top-14 -right-8 h-48 w-48 rounded-full" style={{ background: "radial-gradient(circle, rgba(40,216,255,0.3), rgba(40,216,255,0) 70%)" }} />
                <div className="relative font-mono text-[11px] font-semibold uppercase tracking-[0.22em]" style={{ color: accent }}>Direkter Kontakt</div>
                <div className="relative mt-5 space-y-4 text-sm">
                  <div className="flex items-center gap-3">
                    <span className="grid h-9 w-9 place-items-center rounded-xl border border-white/15 bg-white/10"><Mail size={15} style={{ color: accent }} /></span>
                    <span className="text-slate-200">{contactConfig.emailFallback}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="grid h-9 w-9 place-items-center rounded-xl border border-white/15 bg-white/10"><MapPin size={15} style={{ color: accent }} /></span>
                    <span className="text-slate-200">{contactConfig.address}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="grid h-9 w-9 place-items-center rounded-xl border border-white/15 bg-white/10"><Clock size={15} style={{ color: accent }} /></span>
                    <span className="text-slate-200">Antwort in der Regel innert 24 h</span>
                  </div>
                </div>
                <div className="relative mt-6 border-t border-white/10 pt-4 text-xs text-slate-400">
                  Wir schützen deine Angaben. Keine automatische Weitergabe.
                </div>
              </div>
            </AnimateOnScroll>

            <AnimateOnScroll animation="fade-right" delay={200} className="flex-1">
              <div data-testid="contact-faq-card" className="h-full rounded-2xl bg-[#F4F6F8] p-8">
                <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-[#002D72]">Häufige Fragen</div>
                <Accordion type="single" collapsible className="mt-3">
                  {faqs.map((f, i) => (
                    <AccordionItem key={i} value={`sidebar-item-${i}`} className="border-slate-200">
                      <AccordionTrigger className="text-left text-[#0A111F] hover:no-underline">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </AnimateOnScroll>
          </aside>

          {/* Form */}
          <AnimateOnScroll animation="fade-up" className="order-1 lg:order-2 lg:col-span-7">
            <div className="rounded-2xl border border-slate-200/80 bg-white p-7 shadow-[0_24px_60px_rgba(2,6,23,0.07)] md:p-9">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls}><User size={14} style={{ color: navy }} /> Name</label>
                  <input data-testid="contact-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Dein Name" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}><Mail size={14} style={{ color: navy }} /> E‑Mail</label>
                  <input data-testid="contact-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@firma.ch" className={inputCls} />
                </div>
              </div>
              <div className="mt-4">
                <label className={labelCls}><Building2 size={14} style={{ color: navy }} /> Firma</label>
                <input data-testid="contact-company-input" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Firmenname" className={inputCls} />
              </div>
              <div className="mt-4">
                <label className={labelCls}><Tag size={14} style={{ color: navy }} /> Betreff</label>
                <input data-testid="contact-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Worum geht's?" className={inputCls} />
              </div>
              <div className="mt-4">
                <label className={labelCls}><MessageSquare size={14} style={{ color: navy }} /> Nachricht</label>
                <textarea data-testid="contact-message-input" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Beschreibe kurz deine Situation oder Frage." rows={6} className={inputCls} />
              </div>
              <div className="mt-7">
                <Button
                  data-testid="contact-submit-button"
                  onClick={openMail}
                  className="w-full rounded-full bg-[#002D72] py-6 text-base font-semibold text-white transition-all duration-300 hover:bg-[#28D8FF] hover:text-[#0A111F] hover:shadow-[0_0_24px_rgba(40,216,255,0.45)]"
                >
                  <Send size={16} className="mr-2" /> Anfrage senden
                </Button>
                <p className="mt-3 text-center text-xs text-slate-500">Öffnet dein E‑Mail‑Programm mit vorausgefülltem Inhalt.</p>
              </div>
              {error ? (
                <p data-testid="contact-error" className="mt-3 text-sm font-medium text-red-600">{error}</p>
              ) : null}
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- FOOTER ---------------------------------- */

function Footer({ onImpressum, onPrivacy }) {
  return (
    <footer className="bg-[#0A111F] text-slate-400">
      <div className="h-px w-full" style={{ background: "linear-gradient(90deg, transparent, rgba(40,216,255,0.5), transparent)" }} />
      <div className="mx-auto max-w-7xl px-6 py-16 md:px-12">
        <div className="flex flex-col items-center justify-between gap-10 md:flex-row md:items-center">
          <div className="flex flex-col items-center gap-3 md:items-start">
            <img src="/logo-tight.png" alt="l'azote" className="h-8 w-auto brightness-0 invert opacity-95 md:h-9" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">Security Hardening · Schweiz</span>
          </div>
          <div className="flex items-center gap-8">
            <button data-testid="footer-impressum" onClick={onImpressum} className="text-sm text-slate-300 underline-offset-4 transition-colors hover:text-[#28D8FF] hover:underline">Impressum</button>
            <button data-testid="footer-datenschutz" onClick={onPrivacy} className="text-sm text-slate-300 underline-offset-4 transition-colors hover:text-[#28D8FF] hover:underline">Datenschutzerklärung</button>
          </div>
        </div>
        <div className="mt-12 flex flex-col items-center justify-between gap-2 border-t border-white/10 pt-6 font-mono text-[11px] uppercase tracking-wider text-slate-500 md:flex-row">
          <span>© {new Date().getFullYear()} {brand.name} Kollektivgesellschaft</span>
          <span className="inline-flex items-center gap-2">
            <span className="pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-[#28D8FF]" />
            Status: gehärtet
          </span>
        </div>
      </div>
    </footer>
  );
}

/* ----------------------------------- PAGE ---------------------------------- */

export default function LandingPage() {
  const accent = brand.colors.cyan;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [impressumOpen, setImpressumOpen] = useState(false);
  const privacyRef = useRef(null);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const onScrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div id="top" className="min-h-screen w-full overflow-x-clip bg-white">
      <HeroSection accent={accent} onScrollTo={onScrollTo} />

      <ServicesSection onMore={(s) => { setSelectedService(s); setDialogOpen(true); }} />

      <PackagesSection />

      <TeamSection />

      <QuoteSection accent={accent} />

      <NewsSection />

      <ContactSection accent={accent} />

      <Footer
        onImpressum={() => { setPrivacyOpen(false); setImpressumOpen(true); }}
        onPrivacy={() => { setImpressumOpen(false); setPrivacyOpen(true); }}
      />

      <Dialog open={impressumOpen} onOpenChange={setImpressumOpen}>
        <DialogContent className="sm:max-w-md border-slate-200 bg-white/95 backdrop-blur-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-slate-900">Impressum</DialogTitle>
          </DialogHeader>
          <div className="text-slate-700 space-y-1">
            <p className="font-semibold">l'azote Kollektivgesellschaft</p>
            <p>Weiningen ZH</p>
            <p>UID: CHE-359.018.754</p>
            <p>Vertretungsberechtigte: {team.map(t => t.name).join(", ")}</p>
            <p>E‑Mail: info@lazote.ch</p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
        <DialogContent className="sm:max-w-3xl w-[90vw] max-h-[85vh] overflow-y-auto border-slate-200 bg-white/95 backdrop-blur-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-slate-900">Datenschutzerklärung</DialogTitle>
          </DialogHeader>
          <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            <div className="font-semibold mb-2 text-slate-900">Inhaltsverzeichnis</div>
            <ul className="list-disc pl-5 space-y-1">
              {[
                ["ds-1", "Allgemeine Informationen"],
                ["ds-2", "Verantwortliche Stelle"],
                ["ds-3", "Personendaten"],
                ["ds-4", "Cookies"],
                ["ds-5", "Kontaktformular"],
                ["ds-6", "Weitergabe an Dritte"],
                ["ds-7", "Ihre Rechte"],
                ["ds-8", "Haftungsausschluss"],
                ["ds-9", "Anpassungen und Änderungen"],
                ["ds-10", "Kontakt"],
              ].map(([id, label]) => (
                <li key={id}>
                  <a className="hover:underline" href={`#${id}`} onClick={(e) => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); }}>{label}</a>
                </li>
              ))}
            </ul>
          </div>

          <div ref={privacyRef} className="prose prose-slate max-w-none text-slate-800 prose-h2:text-2xl prose-h3:text-xl prose-p:text-base" dangerouslySetInnerHTML={{ __html: privacyContent
            .replace('<p><strong>Allgemeine Informationen</strong></p>', '<h2 id=\'ds-1\'>Allgemeine Informationen</h2>')
            .replace('<p><strong>Verantwortliche Stelle</strong></p>', '<h2 id=\'ds-2\'>Verantwortliche Stelle</h2>')
            .replace('<p><strong>Personendaten, die erhoben und bearbeitet werden</strong></p>', '<h2 id=\'ds-3\'>Personendaten</h2>')
            .replace('<p><strong>Cookies</strong></p>', '<h2 id=\'ds-4\'>Cookies</h2>')
            .replace('<p><strong>Kontaktformular</strong></p>', '<h2 id=\'ds-5\'>Kontaktformular</h2>')
            .replace('<p><strong>Weitergabe an Dritte</strong></p>', '<h2 id=\'ds-6\'>Weitergabe an Dritte</h2>')
            .replace('<p><strong>Ihre Rechte</strong></p>', '<h2 id=\'ds-7\'>Ihre Rechte</h2>')
            .replace('<p><strong>Haftungsausschluss</strong></p>', '<h2 id=\'ds-8\'>Haftungsausschluss</h2>')
            .replace('<p><strong>Anpassungen und Änderungen</strong></p>', '<h2 id=\'ds-9\'>Anpassungen und Änderungen</h2>')
            .replace('<p><strong>Kontakt</strong></p>', '<h2 id=\'ds-10\'>Kontakt</h2>')
          }} />
        </DialogContent>
      </Dialog>

      <ServiceDialog open={dialogOpen} onOpenChange={setDialogOpen} service={selectedService} onScrollTo={onScrollTo} />
      <ExitIntentPopup />
    </div>
  );
}
